'use client';
import React from 'react';
import { cn } from '@/app/lib/utils';
import { Tag, Typography, AppImage, LinkWrapper } from '@workspace/ui/common';
import { ICaseStudyItem } from '@workspace/interfaces/case-study.types';
import { sanityImageSrc } from '@/sanity/lib/image';

type CardItem = ICaseStudyItem;

interface CaseStudyCardProps {
  product: CardItem;
  onProductClick?: (product: CardItem) => void;
  onTagClick?: (tag: string) => void;
  className?: string;
  showTag?: boolean;
  showDescription?: boolean;
  imageClassName?: string;
}

export function CaseStudyCard({
  product,
  onTagClick,
  className,
  showTag = true,
  showDescription = true,
  imageClassName,
}: CaseStudyCardProps) {
  const { category, description, name, viewDetailLink, tags, image } = product;

  return (
    <LinkWrapper link={viewDetailLink}>
      <div className={cn('flex flex-col items-start gap-4 w-full overflow-hidden', className)}>
        {/* Product Image */}
        <div
          className={cn('relative bg-light-silver aspect-[386/289] overflow-hidden w-full xl:w-96.5', imageClassName)}
        >
          {image?.asset?.url && (
            <AppImage
              src={sanityImageSrc(image, { width: 772, height: 578 })}
              alt={name}
              fill
              className="object-cover transition-transform duration-300 ease-in-out hover:scale-110 cursor-pointer"
            />
          )}
        </div>

        <div className="flex flex-col items-start gap-2 w-full">
          <div className="min-h-8 flex flex-wrap gap-2 w-full">
            {showTag &&
              (tags && tags?.length > 0
                ? tags?.map((t, idx) => (
                    <Tag
                      key={`case-study-tag-${idx}-${t._id}`}
                      onClick={() => onTagClick?.(t._id)}
                      className="flex-shrink-0"
                    >
                      {t.name}
                    </Tag>
                  ))
                : category?.name && (
                    <Tag onClick={() => onTagClick?.(category?.name)} className="flex-shrink-0">
                      {category?.name}
                    </Tag>
                  ))}
          </div>

          <div className="flex flex-col items-start gap-1">
            <Typography variant="h3" styleVariant="h6" className="leading-6.5 text-secondary-400 line-clamp-2">
              {name}
            </Typography>
            {showDescription && description && (
              <Typography variant="bodyMd" className="leading-normal text-black line-clamp-2">
                {description}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </LinkWrapper>
  );
}
